import React, { useRef } from 'react'

import { DocumentNode, useQuery } from '@apollo/client'

import * as R from 'ramda'

import { Box, Container, Empty, Flex, Heading, Text } from 'stardust'
import styled from 'styled-components'

import empty from '~/assets/images/empty.svg'

import Loading from '~/components/Loading/List'
import { HEADER_HEIGHT, LAYERS } from '~/theme'

const ListHeader = styled(Box)`
  align-items: center;
  background: ${(props) => props.theme.colors.surfacePrimary};
  display: flex;
  position: sticky;
  top: ${HEADER_HEIGHT};
  z-index: ${LAYERS.PageHeader};
  box-shadow: ${(props) => props.theme.shadows['1dp']};
  height: 100px;
`

const Error = () => (
  <div>
    <p>Error...</p>
  </div>
)

interface ListViewProps {
  query: DocumentNode
  variables?: Record<string, unknown>
  heading: string
  HeaderActions?: React.ReactNode | (() => React.ReactNode)
  path: string[]
  render: (args: { data: any }) => React.ReactNode
}

const ListView = (props: ListViewProps) => {
  const headerRef = useRef<HTMLHeadingElement>(null)

  const { query, variables, heading, HeaderActions, path, render } = props
  const { data, loading, error } = useQuery(query, {
    variables,
  })

  const header = (
    <ListHeader data-qa="list-heading">
      <Container width={1}>
        <Flex alignItems="center" justifyContent="space-between">
          <Heading.h5 bold caps mb={0} mt={0} ref={headerRef} data-qa="page-title">
            {heading}
          </Heading.h5>
          {typeof HeaderActions === 'function' ? HeaderActions() : HeaderActions || null}
        </Flex>
      </Container>
    </ListHeader>
  )

  if (error) {
    return <Error />
  }

  if (loading) {
    return (
      <Box data-qa="profile-list">
        {header}
        <Loading />
      </Box>
    )
  }

  if (R.lt(R.length(R.path(path, data) as ArrayLike<unknown>), 0)) {
    return (
      <ListHeader boxShadow="1dp" height="72px">
        <Flex flexDirection="column" alignItems="center" justifyContent="center">
          {<Empty heading={heading} image={empty} />}
          <Text.p>{`There are no ${heading} in this service`}</Text.p>
        </Flex>
      </ListHeader>
    )
  }
  if (data) {
    return (
      <Box data-qa="profile-list">
        {header}
        {render({ data })}
      </Box>
    )
  }

  return null
}

ListView.displayName = 'ListView'

export default React.memo(ListView)
